/**
 * Dates and times — the one way the dashboard shows them (en-IN).
 *
 *   formatDate("2026-11-05")                      → "5 Nov 2026"
 *   formatDate("2026-11-05T18:45:00Z")            → "6 Nov 2026"  (India date)
 *   formatDateTime("2026-10-01T04:00:00Z")        → "1 Oct 2026, 9:30 am IST"
 *   formatTime("2026-10-01T04:00:00Z")            → "9:30 am IST"
 *   formatDateRange("2026-11-03", "2026-11-05")   → "3–5 Nov 2026"
 *   formatDateRange("2026-10-23", "2026-10-23")   → "23 Oct 2026"
 *
 * Instants (timestamps) are shown in India time: the product is
 * India-only, and a laptop set to another zone must not move a punch, a
 * decision or a deadline to another day. A bare calendar date
 * ("2026-11-05") is shown as that date in every zone — `new Date(
 * "2026-11-05")` is UTC midnight, which a browser west of Greenwich would
 * print as the 4th.
 *
 * Every function tolerates null / undefined / unparseable input: "—" (or
 * the fallback passed) for empty, the raw value for garbage — never
 * "Invalid Date".
 *
 * Money is not formatted here: lib/expense-format formatPaise (paise
 * precision — "₹21,359.20", never "₹21,359.2").
 *
 * Plain .ts with no imports so `npm run test:unit` can load it
 * (format.test.ts).
 */

export type DateInput = string | Date | null | undefined;

const IST = "Asia/Kolkata";
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const dateParts = {
  ist: new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: IST,
  }),
  utc: new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }),
};

const timeParts = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: IST,
});

interface Parsed {
  date: Date;
  /** A bare YYYY-MM-DD: a calendar date, not an instant. */
  dateOnly: boolean;
}

function parse(v: DateInput): Parsed | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : { date: v, dateOnly: false };
  }
  const s = v.trim();
  const m = DATE_ONLY.exec(s);
  if (m) {
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: true };
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: false };
}

interface DayParts {
  day: string;
  month: string;
  year: string;
}

function dayParts(p: Parsed): DayParts {
  const fmt = p.dateOnly ? dateParts.utc : dateParts.ist;
  const out: DayParts = { day: "", month: "", year: "" };
  for (const part of fmt.formatToParts(p.date)) {
    if (part.type === "day" || part.type === "month" || part.type === "year") {
      out[part.type] = part.value;
    }
  }
  return out;
}

function joinDay(d: DayParts): string {
  return `${d.day} ${d.month} ${d.year}`;
}

function rawOr(v: DateInput, fallback: string): string {
  if (v == null || v === "") return fallback;
  return v instanceof Date ? fallback : v;
}

/** "5 Nov 2026" — a calendar date as given, or an instant's India date. */
export function formatDate(v: DateInput, fallback = "—"): string {
  const p = parse(v);
  return p ? joinDay(dayParts(p)) : rawOr(v, fallback);
}

/** "9:30 am IST" — an instant's India time ("—" for a bare date). */
export function formatTime(v: DateInput, fallback = "—"): string {
  const p = parse(v);
  if (!p) return rawOr(v, fallback);
  if (p.dateOnly) return fallback;
  let hour = "";
  let minute = "";
  let period = "";
  for (const part of timeParts.formatToParts(p.date)) {
    if (part.type === "hour") hour = part.value;
    else if (part.type === "minute") minute = part.value;
    else if (part.type === "dayPeriod") period = part.value.toLowerCase();
  }
  return `${hour}:${minute}${period ? ` ${period}` : ""} IST`;
}

const timeSecondsParts = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
  timeZone: IST,
});

/**
 * "7 Oct 2026, 7:27:24 pm IST" — formatDateTime with seconds, for logs
 * where two events can share a minute (the audit log). A bare date renders
 * as formatDate.
 */
export function formatDateTimeSeconds(v: DateInput, fallback = "—"): string {
  const p = parse(v);
  if (!p) return rawOr(v, fallback);
  if (p.dateOnly) return joinDay(dayParts(p));
  let hour = "";
  let minute = "";
  let second = "";
  let period = "";
  for (const part of timeSecondsParts.formatToParts(p.date)) {
    if (part.type === "hour") hour = part.value;
    else if (part.type === "minute") minute = part.value;
    else if (part.type === "second") second = part.value;
    else if (part.type === "dayPeriod") period = part.value.toLowerCase();
  }
  return `${joinDay(dayParts(p))}, ${hour}:${minute}:${second}${period ? ` ${period}` : ""} IST`;
}

/** "1 Oct 2026, 9:30 am IST" — a bare date renders as formatDate. */
export function formatDateTime(v: DateInput, fallback = "—"): string {
  const p = parse(v);
  if (!p) return rawOr(v, fallback);
  if (p.dateOnly) return joinDay(dayParts(p));
  return `${joinDay(dayParts(p))}, ${formatTime(p.date)}`;
}

/**
 * A from–to span: "23 Oct 2026" when both ends are the same day,
 * "3–5 Nov 2026" within a month, "28 Oct – 2 Nov 2026" within a year,
 * "30 Dec 2026 – 2 Jan 2027" across years. A missing end renders the
 * start alone.
 */
export function formatDateRange(from: DateInput, to: DateInput, fallback = "—"): string {
  const a = parse(from);
  const b = parse(to);
  if (!a) return b ? formatDate(to) : rawOr(from, fallback);
  if (!b) return formatDate(from);
  const x = dayParts(a);
  const y = dayParts(b);
  if (x.year !== y.year) return `${joinDay(x)} – ${joinDay(y)}`;
  if (x.month !== y.month) return `${x.day} ${x.month} – ${joinDay(y)}`;
  if (x.day !== y.day) return `${x.day}–${y.day} ${y.month} ${y.year}`;
  return joinDay(x);
}

/** Count noun for a number of days — "1 day", "2.5 days", "30 days" (never "day(s)"). */
export function dayCount(n: number): string {
  const v = Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  return `${v} ${n === 1 ? "day" : "days"}`;
}

const istDayKey = new Intl.DateTimeFormat("en-CA", {
  timeZone: IST,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The India (IST) calendar day of an instant as YYYY-MM-DD — "today" when
 *  called with no argument. The browser's own zone never decides the day. */
export function istDateKey(v: Date | string = new Date()): string {
  const d = typeof v === "string" ? new Date(v) : v;
  return Number.isNaN(d.getTime()) ? "" : istDayKey.format(d);
}

/** The whole calendar month before `todayISO` (YYYY-MM-DD) — the usual
 *  range for a month-end export. */
export function previousMonthRange(todayISO: string): { from: string; to: string } {
  const [y, m] = todayISO.split("-").map(Number);
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const mm = String(pm).padStart(2, "0");
  return { from: `${py}-${mm}-01`, to: `${py}-${mm}-${String(last).padStart(2, "0")}` };
}

/** The month a books export opens on (usability review 2026-10-20: it
 *  opened on last month, which is empty for a new company): from the 15th
 *  the current month, before it last month — the month just closed. */
export function booksExportRange(todayISO: string): { from: string; to: string } {
  const [y, m, d] = todayISO.split("-").map(Number);
  if (d < 15) return previousMonthRange(todayISO);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, "0")}` };
}
